import { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const HeroManager = () => {
  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHero();
  }, []);

  const fetchHero = async () => {
    try {
      const response = await adminApi.getHero();
      setHero(response.data);
    } catch (error) {
      console.error('Error fetching hero:', error);
      toast.error('Failed to load hero content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setHero({ ...hero, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateHero(hero);
      toast.success('Hero section updated successfully');
    } catch (error) {
      console.error('Error saving hero:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="hero-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Hero Section</h1>
          <p className="text-muted-foreground">Manage your landing page hero content.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-hero-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="admin-card space-y-6">
        <div className="form-group">
          <label htmlFor="title" className="form-label">Title</label>
          <Input
            id="title"
            name="title"
            value={hero?.title || ''}
            onChange={handleChange}
            placeholder="We Build Digital Experiences"
            className="input-neon"
            data-testid="hero-title-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="subtitle" className="form-label">Subtitle</label>
          <Textarea
            id="subtitle"
            name="subtitle"
            value={hero?.subtitle || ''}
            onChange={handleChange}
            placeholder="Transform your vision into reality..."
            rows={3}
            className="input-neon resize-none"
            data-testid="hero-subtitle-input"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="form-group">
            <label htmlFor="cta_text" className="form-label">CTA Button Text</label>
            <Input
              id="cta_text"
              name="cta_text"
              value={hero?.cta_text || ''}
              onChange={handleChange}
              placeholder="Get Started"
              className="input-neon"
              data-testid="hero-cta-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="cta_link" className="form-label">CTA Button Link</label>
            <Input
              id="cta_link"
              name="cta_link"
              value={hero?.cta_link || ''}
              onChange={handleChange}
              placeholder="#contact"
              className="input-neon"
              data-testid="hero-link-input"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="image_url" className="form-label">Background Image URL (optional)</label>
          <Input
            id="image_url"
            name="image_url"
            value={hero?.image_url || ''}
            onChange={handleChange}
            placeholder="https://..."
            className="input-neon"
            data-testid="hero-image-input"
          />
        </div>
      </div>
    </div>
  );
};

export default HeroManager;
